import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Ticket, DollarSign, Hammer, Zap, Eye, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { eventDetails, heroStats, heroFeatures, marketingHighlights } from '../data/mock';

const CountdownTimer = ({ targetDate }) => {
  const calculateTimeLeft = React.useCallback(() => {
    const difference = new Date(targetDate) - new Date();
    if (difference <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  }, [targetDate]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  return (
    <div className="flex gap-2 sm:gap-3">
      {Object.entries(timeLeft).map(([key, value]) => (
        <div key={key} className="countdown-box">
          <div className="countdown-value text-lg sm:text-xl md:text-2xl">{String(value).padStart(2, '0')}</div>
          <div className="countdown-label text-[8px] sm:text-[10px]">{key}</div>
        </div>
      ))}
    </div>
  );
};

const iconMap = { DollarSign, Hammer, Zap };

const HeroSection = () => {
  const VIDEO_URL = 'https://customer-assets.emergentagent.com/job_defi-protocol-summit/artifacts/gmej8bdg_Event%20experience%204k.mp4';

  return (
    <section className="relative min-h-screen flex items-center pt-16 sm:pt-20 pb-12 sm:pb-16 overflow-hidden" data-testid="hero-section">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[var(--accent-primary)] opacity-5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-56 sm:w-80 h-56 sm:h-80 bg-[var(--accent-primary)] opacity-5 rounded-full blur-3xl" />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `linear-gradient(rgba(218, 255, 1, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(218, 255, 1, 0.3) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-10 xl:gap-14 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Edition badge with date */}
            <div className="flex flex-wrap items-center gap-1.5 xs:gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className="flex items-center gap-1.5 xs:gap-2 px-2 xs:px-3 py-1 xs:py-1.5 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                <span className="text-[10px] xs:text-xs text-[var(--text-muted)]">LayerSeek Edition:</span>
                <span className="text-[10px] xs:text-xs font-semibold text-[var(--accent-primary)]">DeFi</span>
              </div>
              <div className="flex items-center gap-1.5 xs:gap-2 px-2 xs:px-3 py-1 xs:py-1.5 rounded-full border border-[var(--accent-primary)] bg-[var(--accent-bg)]">
                <span className="w-1.5 h-1.5 xs:w-2 xs:h-2 rounded-full bg-[var(--accent-primary)] animate-pulse" />
                <span className="text-[10px] xs:text-xs font-medium text-[var(--accent-primary)]">{eventDetails.date}</span>
              </div>
              <div className="flex items-center gap-1 xs:gap-1.5 px-2 xs:px-3 py-1 xs:py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10">
                <Globe className="w-2.5 h-2.5 xs:w-3 xs:h-3 text-cyan-400" />
                <span className="text-[10px] xs:text-xs font-medium text-cyan-400">Virtual Event</span>
              </div>
            </div>
            
            {/* Headline - Main + Sub tagline */}
            <h1 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-2 break-words">
              {eventDetails.headline}
            </h1>
            <p className="text-xs xs:text-sm sm:text-base md:text-lg lg:text-xl text-[var(--accent-primary)] font-medium mb-2 sm:mb-3 break-words">
              Unlock Grants, Ship Integrations & Skyrocket your BD
            </p>
            <p className="text-[10px] xs:text-xs sm:text-sm text-[var(--text-muted)] italic mb-3 sm:mb-4 md:mb-6">
              If You Build in DeFi, This is for you
            </p>
            
            {/* Subheadline */}
            <p className="text-sm sm:text-base md:text-lg text-[var(--text-secondary)] mb-6 max-w-xl">
              {eventDetails.subheadline}
            </p>
            
            {/* Marketing Highlights - Key Value Props */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
              {marketingHighlights.map((highlight, index) => {
                const Icon = iconMap[highlight.icon];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.15 + index * 0.1 }}
                    className="p-2.5 sm:p-3 rounded-xl bg-[var(--bg-secondary)]/50 border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] transition-all group text-center"
                  >
                    <div className="w-8 h-8 sm:w-9 sm:h-9 mx-auto rounded-lg bg-[var(--accent-bg)] flex items-center justify-center mb-1.5 group-hover:scale-110 transition-transform">
                      <Icon className="w-4 h-4 text-[var(--accent-primary)]" />
                    </div>
                    <h3 className="font-semibold text-white text-[10px] sm:text-xs">{highlight.title}</h3>
                    <p className="text-[9px] sm:text-[10px] text-[var(--text-muted)] leading-tight mt-0.5 hidden sm:block">{highlight.description}</p>
                  </motion.div>
                );
              })}
            </div>
            
            {/* Features list - compact */}
            <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-6">
              {heroFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-1.5 text-[var(--text-secondary)]"
                >
                  <Check className="w-3.5 h-3.5 text-[var(--accent-primary)] flex-shrink-0" />
                  <span className="text-[10px] sm:text-xs">{feature}</span>
                </motion.div>
              ))}
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <a
                href="https://luma.com/6kdsrxks"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  className="w-full sm:w-auto bg-[var(--accent-primary)] text-[var(--bg-primary)] hover:bg-[var(--accent-hover)] h-11 sm:h-12 px-5 sm:px-6 rounded-xl text-sm font-semibold transition-all hover:-translate-y-1 hover:shadow-[0_8px_25px_rgba(218,255,1,0.3)]"
                  data-testid="get-tickets-btn"
                >
                  <Ticket className="w-4 h-4 mr-2" />
                  Get Tickets
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="#experience">
                <Button 
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-[var(--border-primary)] text-white hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] hover:bg-[var(--accent-bg)] h-11 sm:h-12 px-5 sm:px-6 rounded-xl text-sm font-semibold transition-all"
                  data-testid="view-experience-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View the Experience
                </Button>
              </a>
            </div>
            
            {/* Countdown */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <p className="text-[10px] sm:text-xs text-[var(--text-muted)] uppercase tracking-wider">Event starts in</p>
                <span className="text-[9px] sm:text-[10px] text-orange-400 font-medium">Once-a-year opportunity</span>
              </div>
              <CountdownTimer targetDate={eventDetails.targetDate} />
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-4 gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-[var(--border-subtle)]">
              {heroStats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-lg sm:text-xl md:text-2xl font-bold text-[var(--accent-primary)]">{stat.value}</div>
                  <div className="text-[9px] sm:text-xs text-[var(--text-muted)]">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* Right - Video */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative lg:-mr-4 xl:-mr-10"
          >
            {/* Video container with border glow */}
            <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden border border-[var(--accent-primary)]/30 shadow-[0_10px_60px_rgba(218,255,1,0.15)]">
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full aspect-video object-cover"
                style={{ filter: 'brightness(1.05) contrast(1.05)' }}
              >
                <source src={VIDEO_URL} type="video/mp4" />
              </video>
              
              {/* Overlay gradient for brand colors */}
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-primary)]/40 via-transparent to-transparent pointer-events-none" />
              
              {/* Live badge */}
              <div className="absolute top-3 left-3 sm:top-4 sm:left-4 flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 bg-[var(--bg-primary)]/80 backdrop-blur-sm rounded-full border border-[var(--accent-primary)]/30">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)] animate-pulse" />
                <span className="text-[10px] sm:text-xs font-medium text-[var(--accent-primary)]">Virtual Experience</span>
              </div>
            </div>
            
            {/* Caption */}
            <p className="text-center text-[10px] sm:text-xs text-[var(--text-muted)] mt-3">
              Real-time networking virtually, not a boring webinar
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
